import React, { useState } from 'react';
import { SecretCell } from './SecretCell';
import { InlineCellEditor } from './InlineCellEditor';
import { FloatingActionBar } from './FloatingActionBar';

export function TableRow({
    row,
    columns,
    pkCols,
    fks,
    fkOptions = {},
    isPolymorphicTable,
    polymorphicLabels,
    handleDeleteClick,
    schemaWarning,
    editingCell,
    setEditingCell,
    submitCellEdit,
    draggingCol,
    dragOverCol,
    isExpanded,
    toggleExpand,
    hasChildren,
    index,
    token
}) {
    const hasPks = pkCols && pkCols.length > 0;
    const pkVal = hasPks ? JSON.stringify(pkCols.reduce((acc, c) => { acc[c.ColumnName] = row[c.ColumnName]; return acc; }, {})) : null;
    const [isHovered, setIsHovered] = useState(false);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    const copyToClipboard = (text) => {
        if (text === null || text === undefined) return;
        const cleanText = String(text);
        const textarea = document.createElement('textarea');
        textarea.value = cleanText;
        textarea.style.position = 'fixed'; 
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try { document.execCommand('copy'); } catch (err) {}
        document.body.removeChild(textarea);
    };

    const copyJsonToClipboard = (e) => {
        e.stopPropagation();
        copyToClipboard(JSON.stringify(row, null, 2));
    };

    return (
        <tr 
            className={`border-b border-slate-700 hover:bg-slate-700/80 transition-colors relative group/row ${
                index % 2 === 0 ? 'bg-slate-900/30' : 'bg-transparent'
            } ${isExpanded ? 'bg-slate-800/50' : ''}`}
            onMouseMove={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setMousePos({ x: e.clientX, y: rect.top });
            }}
            draggable={pkVal !== null}
            onDragStart={(e) => {
                if (pkVal === null) {
                    e.preventDefault();
                    return;
                }
                // Determine what group this row belongs to natively (if any)
                // Actually, the drag target is just the PK. We don't even need the source group.
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('sapphire/row-pk', pkVal);
                e.dataTransfer.setData('sapphire/row-data', JSON.stringify(row));
                // Add styling to make it look like a floating card
                const el = e.target.cloneNode(true);
                el.style.opacity = '0.5';
                el.style.backgroundColor = '#1e293b';
                document.body.appendChild(el);
                e.dataTransfer.setDragImage(el, 20, 20);
                setTimeout(() => document.body.removeChild(el), 0);
            }}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Dedicated TD for expand chevron and grouping */}
            <td className="px-2 py-3 bg-slate-900/50 border-r border-slate-700/50 w-8" style={row._treeDepth ? { paddingLeft: `${(row._treeDepth * 1) + 0.5}rem` } : {}}>
                <div className="flex items-center justify-end shrink-0 gap-1 w-full">
                    {row._groupCount !== undefined && (
                        <span className="text-[10px] bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded-full font-mono border border-slate-600 shadow-sm ml-auto mr-1">
                            {row._groupCount}
                        </span>
                    )}
                    {(row._isTreeNode || hasChildren) && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if(toggleExpand) toggleExpand();
                            }}
                            disabled={!hasChildren}
                            className={`flex items-center justify-center transition-colors shrink-0 ${hasChildren ? 'text-teal-400 hover:text-teal-300' : 'text-slate-500 cursor-default'}`}
                        >
                            <span className="material-symbols-outlined text-[16px]">
                                {hasChildren 
                                    ? (isExpanded ? 'expand_more' : 'chevron_right')
                                    : 'horizontal_rule'}
                            </span>
                        </button>
                    )}
                </div>
            </td>

            {columns.map((col, index) => {
                const colName = col.ColumnName;
                const isEditingThisCell = editingCell?.rowKey === pkVal && editingCell?.colName === colName;

                const isFk = fks.some(fk => fk.ColumnName === colName);
                let displayValue = row[colName];

                if (isPolymorphicTable && colName === 'TargetRecordID') {
                    const polyKey = `${row.TargetSchema}.${row.TargetTable}.${row.TargetRecordID}`;
                    if (polymorphicLabels[polyKey]) {
                        displayValue = polymorphicLabels[polyKey];
                    }
                }

                const isSecret = colName.toLowerCase().includes('password') || colName.toLowerCase().includes('passwordhash') || colName.toLowerCase().includes('secret');

                if (isEditingThisCell && pkVal !== null) {
                    return (
                        <td key={colName} className="px-4 py-2">
                            <InlineCellEditor 
                                row={row} 
                                colName={colName} 
                                initialValue={displayValue}
                                fks={fks}
                                onSubmit={(finalColName, newValue) => submitCellEdit(row, finalColName, newValue)}
                                onCancel={() => setEditingCell(null)}
                                token={token}
                            />
                        </td>
                    );
                }

                if (isSecret) {
                    return (
                        <SecretCell
                            key={colName}
                            value={displayValue}
                            onCopy={() => copyToClipboard(displayValue)}
                            onDoubleClick={() => {
                                if (!schemaWarning && pkVal !== null && !col.IsIdentity && !col.IsPrimaryKey) {
                                    setEditingCell({ rowKey: pkVal, colName });
                                }
                            }}
                        />
                    );
                }

                const isDragging = draggingCol === colName;
                const isDragOver = dragOverCol === colName && !isDragging;
                const dragClasses = `${isDragging ? 'opacity-0' : 'opacity-100'} ${isDragOver ? 'border-l-4 border-blue-500' : ''}`;

                return (
                    <td 
                        key={colName} 
                        onDoubleClick={() => {
                            if (!schemaWarning && pkVal !== null && !col.IsIdentity && !col.IsPrimaryKey) {
                                setEditingCell({ rowKey: pkVal, colName });
                            }
                        }}
                        onClick={() => copyToClipboard(displayValue)}
                        title="Double-click to edit, Single-click to copy"
                        className={`px-4 py-3 truncate max-w-xs cursor-text hover:bg-slate-700/30 transition-colors group group/cell relative ${dragClasses}`}
                    >
                        <div className="flex items-center gap-2">
                            
                            {isFk && !isSecret && (
                                <span className="material-symbols-outlined text-[14px] text-blue-400 opacity-50 shrink-0" title={`Foreign Key: ${colName}`}>
                                    link
                                </span>
                            )}
                            <div className="overflow-hidden text-ellipsis whitespace-nowrap group-hover/cell:whitespace-normal group-hover/cell:bg-slate-800 group-hover/cell:z-[99] group-hover/cell:relative group-hover/cell:rounded group-hover/cell:shadow-lg transition-all" title={displayValue}>
                                {displayValue === null || displayValue === undefined ? <span className="text-slate-500 italic">null</span> : String(displayValue)}
                            </div>
                        </div>
                    </td>
                );
            })}
            
            <td className="sticky right-0 bg-slate-800 border-l border-slate-700/50 px-3 py-2 w-[50px] shrink-0 text-center shadow-[-5px_0_15px_-5px_rgba(0,0,0,0.3)] z-10 group-hover/row:bg-slate-700/90 transition-colors">
                {pkVal != null && (
                    <FloatingActionBar 
                        menuItems={[
                            {
                                label: "Delete Record",
                                icon: "delete",
                                danger: true,
                                onClick: () => handleDeleteClick(row)
                            }
                        ]}
                        position="bottom-right"
                    />
                )}
            </td>
        </tr>
    );
}
